import React, { useMemo, useState } from 'react';
import { Button, Space, Table, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import ResourceFilterArea, { DEFAULT_FILTERS, type FilterOptions } from './components/ResourceFilterArea';
import ResourceDrawer from './components/ResourceDrawer';
import ResourceAccountsDrawer from './components/ResourceAccountsDrawer';
import { QUERY_RESOURCE_BY_PAGE, type QueryResourceByPageResponse, type ResourceType } from './graphql/queries/queryResourceByPage';
import { LIST_ALL_RESOURCES, type ListAllResourcesResponse } from './graphql/queries/listAllResources';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

const ResourcePage: React.FC = () => {
  const { t } = useTranslation();

  /** 筛选输入草稿：输入/选择时不触发列表查询 */
  const [draftFilters, setDraftFilters] = useState<FilterOptions>(DEFAULT_FILTERS);
  /** 已应用筛选：仅在点击“筛选/重置”后触发列表查询 */
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTERS);

  const { data, loading, refetch } = useQuery<ListAllResourcesResponse>(LIST_ALL_RESOURCES, {
    fetchPolicy: 'cache-and-network',
  });

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [drawerParentId, setDrawerParentId] = useState(0);
  const [drawerParentDisplayName, setDrawerParentDisplayName] = useState('');
  const [editRecord, setEditRecord] = useState<ResourceType | null>(null);
  const [accountsDrawerVisible, setAccountsDrawerVisible] = useState(false);
  const [accountsDrawerResourceCode, setAccountsDrawerResourceCode] = useState('');
  const [accountsDrawerResourceName, setAccountsDrawerResourceName] = useState('');
  const [manualExpandedRowKeys, setManualExpandedRowKeys] = useState<React.Key[]>([]);
  const [manualExpandedRowKeysInFilter, setManualExpandedRowKeysInFilter] = useState<React.Key[]>([]);
  const [pendingExpandParentId, setPendingExpandParentId] = useState<string | null>(null);
  const [queryResourceByPage] = useLazyQuery<QueryResourceByPageResponse>(QUERY_RESOURCE_BY_PAGE, {
    fetchPolicy: 'no-cache',
  });

  const resolveParentDisplayNameForRecord = (record: ResourceType): string => {
    const pid = record.parentId != null ? Number(record.parentId) : 0;
    if (pid === 0) {
      return String(t('central.platformPermission.resource.rootParentName'));
    }
    return record.parentName || String(t('central.platformPermission.resource.parentNameUnknown'));
  };

  const columns: TableColumnsType<ResourceType> = useMemo(
    () => [
      {
        title: t('central.platformPermission.resource.table.id'),
        dataIndex: 'id',
        key: 'id',
        width: 60,
        render: (_: unknown, record: ResourceType) => (
          <Button
            type="link"
            onClick={async () => {
              const id = Number(record.id);
              const res = await queryResourceByPage({
                variables: {
                  condition: { id },
                  pageRequest: { page: 1, size: 1 },
                },
              });
              const latestRecord = res.data?.queryResourceByPage?.content?.[0] ?? record;
              setEditRecord(latestRecord);
              setDrawerMode('edit');
              setDrawerParentId(latestRecord.parentId != null ? Number(latestRecord.parentId) : 0);
              setDrawerParentDisplayName(resolveParentDisplayNameForRecord(latestRecord));
              setDrawerVisible(true);
            }}
            className="px-0 h-auto leading-5 align-middle"
          >
            {record.id ?? '-'}
          </Button>
        ),
      },
      {
        title: t('central.platformPermission.resource.table.name'),
        dataIndex: 'name',
        key: 'name',
        width: 220,
        render: (v) => v || '-',
      },
      {
        title: t('central.platformPermission.resource.table.code'),
        dataIndex: 'code',
        key: 'code',
        width: 160,
        render: (v) => v || '-',
      },
      {
        title: t('central.platformPermission.resource.table.description'),
        dataIndex: 'description',
        key: 'description',
        width: 220,
        ellipsis: { showTitle: false },
        render: (v: string | null) => (
          <Typography.Text style={{ display: 'block' }} ellipsis={{ tooltip: v || '-' }}>
            {v || '-'}
          </Typography.Text>
        ),
      },
      {
        title: t('components.button.action'),
        key: 'operation',
        width: 180,
        render: (_: unknown, record: ResourceType) => (
          <Space size={12}>
            <HasResource resourceCodes={[ResourceCode.resource.ADD]}>
              <Button
                type="link"
                className="px-0"
                onClick={() => {
                  setPendingExpandParentId(String(record.id));
                  setDrawerMode('create');
                  setDrawerParentId(Number(record.id));
                  setDrawerParentDisplayName(record.name ?? String(t('central.platformPermission.resource.parentNameUnknown')));
                  setEditRecord(null);
                  setDrawerVisible(true);
                }}
              >
                {t('central.platformPermission.resource.addChildButton')}
              </Button>
            </HasResource>
            <HasResource resourceCodes={[ResourceCode.menu.RESOURCE_QUERY]}>
              <Button
                type="link"
                className="px-0"
                onClick={() => {
                  setAccountsDrawerResourceCode(record.code);
                  setAccountsDrawerResourceName(record.name ?? '');
                  setAccountsDrawerVisible(true);
                }}
              >
                {t('central.platformPermission.resource.viewAccountsButton')}
              </Button>
            </HasResource>
          </Space>
        ),
      },
    ],
    [t]
  );

  const treeData = useMemo(() => {
    const list = data?.listAllResources ?? [];
    const toKey = (value: number | string | null | undefined): string => {
      const raw = String(value ?? '').trim();
      if (!raw) return '0';
      const numeric = Number(raw);
      return Number.isFinite(numeric) ? String(numeric) : raw;
    };
    const idSet = new Set<string>();
    const childrenByParent = new Map<string, ResourceType[]>();
    list.forEach((item) => {
      const idKey = toKey(item.id);
      const parentKey = toKey(item.parentId);
      idSet.add(idKey);
      const siblings = childrenByParent.get(parentKey) ?? [];
      siblings.push(item);
      childrenByParent.set(parentKey, siblings);
    });

    const rootItems = list.filter((item) => {
      const parentKey = toKey(item.parentId);
      return parentKey === '0' || !idSet.has(parentKey);
    });

    const buildNode = (item: ResourceType): ResourceType & { children?: ResourceType[] } => {
      const currentIdKey = toKey(item.id);
      const children = (childrenByParent.get(currentIdKey) ?? []).map((child) => buildNode(child));
      if (children.length > 0) {
        return { ...item, children };
      }
      return { ...item };
    };

    return rootItems.map((item) => buildNode(item));
  }, [data?.listAllResources]);

  const filteredTreeData = useMemo(() => {
    const idKeyword = appliedFilters.id.trim();
    const codeKeyword = appliedFilters.code.trim().toLowerCase();
    const nameKeyword = appliedFilters.name.trim().toLowerCase();
    const descKeyword = appliedFilters.description.trim().toLowerCase();
    const noFilter = !idKeyword && !codeKeyword && !nameKeyword && !descKeyword;
    if (noFilter) return treeData;

    const match = (node: ResourceType) => {
      const idText = String(node.id ?? '');
      const codeText = String(node.code ?? '').toLowerCase();
      const nameText = String(node.name ?? '').toLowerCase();
      const descText = String(node.description ?? '').toLowerCase();
      return (
        (!idKeyword || idText === idKeyword) &&
        (!codeKeyword || codeText.includes(codeKeyword)) &&
        (!nameKeyword || nameText.includes(nameKeyword)) &&
        (!descKeyword || descText.includes(descKeyword))
      );
    };

    const filterNode = (node: ResourceType & { children?: ResourceType[] }): (ResourceType & { children?: ResourceType[] }) | null => {
      const nextChildren = (node.children ?? [])
        .map((child) => filterNode(child as ResourceType & { children?: ResourceType[] }))
        .filter((child): child is ResourceType & { children?: ResourceType[] } => child != null);

      if (match(node) || nextChildren.length > 0) {
        if (nextChildren.length > 0) {
          return { ...node, children: nextChildren };
        }
        return { ...node };
      }
      return null;
    };

    return treeData
      .map((root) => filterNode(root as ResourceType & { children?: ResourceType[] }))
      .filter((root): root is ResourceType & { children?: ResourceType[] } => root != null);
  }, [treeData, appliedFilters]);

  const hasAppliedFilter = useMemo(() => {
    return Boolean(
      appliedFilters.id.trim() ||
      appliedFilters.code.trim() ||
      appliedFilters.name.trim() ||
      appliedFilters.description.trim()
    );
  }, [appliedFilters]);

  const matchedRowKeys = useMemo(() => {
    const list = data?.listAllResources ?? [];
    const idKeyword = appliedFilters.id.trim();
    const codeKeyword = appliedFilters.code.trim().toLowerCase();
    const nameKeyword = appliedFilters.name.trim().toLowerCase();
    const descKeyword = appliedFilters.description.trim().toLowerCase();
    const noFilter = !idKeyword && !codeKeyword && !nameKeyword && !descKeyword;
    if (noFilter) return new Set<string>();
    const set = new Set<string>();
    list.forEach((node) => {
      const idText = String(node.id ?? '');
      const codeText = String(node.code ?? '').toLowerCase();
      const nameText = String(node.name ?? '').toLowerCase();
      const descText = String(node.description ?? '').toLowerCase();
      const matched =
        (!idKeyword || idText === idKeyword) &&
        (!codeKeyword || codeText.includes(codeKeyword)) &&
        (!nameKeyword || nameText.includes(nameKeyword)) &&
        (!descKeyword || descText.includes(descKeyword));
      if (matched) {
        set.add(String(node.id));
      }
    });
    return set;
  }, [data?.listAllResources, appliedFilters]);

  const parentById = useMemo(() => {
    const list = data?.listAllResources ?? [];
    const map = new Map<string, string>();
    list.forEach((item) => {
      map.set(String(item.id), String(item.parentId ?? 0));
    });
    return map;
  }, [data?.listAllResources]);

  const childrenById = useMemo(() => {
    const map = new Map<string, string[]>();
    const walk = (nodes: Array<ResourceType & { children?: ResourceType[] }>) => {
      nodes.forEach((node) => {
        const id = String(node.id);
        const children = (node.children ?? []).map((child) => String(child.id));
        map.set(id, children);
        if (node.children && node.children.length > 0) {
          walk(node.children as Array<ResourceType & { children?: ResourceType[] }>);
        }
      });
    };
    walk(treeData as Array<ResourceType & { children?: ResourceType[] }>);
    return map;
  }, [treeData]);

  const autoExpandedRowKeys = useMemo(() => {
    if (!hasAppliedFilter) return [];
    const visibleKeySet = new Set<string>();
    const walkVisible = (nodes: Array<ResourceType & { children?: ResourceType[] }>) => {
      nodes.forEach((node) => {
        visibleKeySet.add(String(node.id));
        if (node.children && node.children.length > 0) {
          walkVisible(node.children as Array<ResourceType & { children?: ResourceType[] }>);
        }
      });
    };
    walkVisible(filteredTreeData as Array<ResourceType & { children?: ResourceType[] }>);

    const expanded = new Set<string>();
    matchedRowKeys.forEach((id) => {
      let parent = parentById.get(id);
      const seen = new Set<string>();
      while (parent && parent !== '0' && !seen.has(parent)) {
        seen.add(parent);
        if (visibleKeySet.has(parent)) {
          expanded.add(parent);
        }
        parent = parentById.get(parent);
      }
    });
    return Array.from(expanded);
  }, [filteredTreeData, hasAppliedFilter, matchedRowKeys, parentById]);

  const expandedRowKeysInFilter = useMemo(() => {
    const allowed = new Set<string>();
    const walk = (nodes: Array<ResourceType & { children?: ResourceType[] }>) => {
      nodes.forEach((node) => {
        allowed.add(String(node.id));
        if (node.children && node.children.length > 0) {
          walk(node.children as Array<ResourceType & { children?: ResourceType[] }>);
        }
      });
    };
    walk(filteredTreeData as Array<ResourceType & { children?: ResourceType[] }>);
    const merged = new Set<string>([
      ...autoExpandedRowKeys.map((k) => String(k)),
      ...manualExpandedRowKeysInFilter.map((k) => String(k)),
    ]);
    return Array.from(merged).filter((k) => allowed.has(k));
  }, [autoExpandedRowKeys, manualExpandedRowKeysInFilter, filteredTreeData]);

  const handleRefresh = async () => {
    await refetch();
    if (pendingExpandParentId && pendingExpandParentId !== '0') {
      setManualExpandedRowKeys((prev) => Array.from(new Set([...prev, pendingExpandParentId])));
      setManualExpandedRowKeysInFilter((prev) => Array.from(new Set([...prev, pendingExpandParentId])));
      setPendingExpandParentId(null);
    }
  };

  const openRootAdd = () => {
    setPendingExpandParentId(null);
    setDrawerMode('create');
    setDrawerParentId(0);
    setDrawerParentDisplayName(String(t('central.platformPermission.resource.rootParentName')));
    setEditRecord(null);
    setDrawerVisible(true);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
  };

  const handleResetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setManualExpandedRowKeys([]);
    setManualExpandedRowKeysInFilter([]);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <ResourceFilterArea
        filters={draftFilters}
        setFilters={setDraftFilters}
      />

      <div className="flex justify-between items-center gap-2 mb-3 pl-0">
        <div className="flex items-center gap-2">
          <HasResource resourceCodes={[ResourceCode.resource.ADD]}>
            <Button type="primary" onClick={openRootAdd} className="w-auto min-w-24">
              <span className="font-bold">{t('central.platformPermission.resource.addButton')}</span>
            </Button>
          </HasResource>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            onClick={() => {
              handleApplyFilters();
            }}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button
            onClick={() => {
              handleResetFilters();
            }}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <Table<ResourceType>
        columns={columns}
        dataSource={filteredTreeData}
        loading={loading}
        rowKey={(record) => String(record.id)}
        childrenColumnName="children"
        pagination={false}
        expandable={{
          expandIconColumnIndex: 1,
          indentSize: 24,
          expandedRowKeys: hasAppliedFilter ? expandedRowKeysInFilter : manualExpandedRowKeys,
          onExpand: (expanded, record) => {
            const recordId = String(record.id);
            const updateExpandedKeys = (updater: (prev: React.Key[]) => React.Key[]) => {
              if (hasAppliedFilter) {
                setManualExpandedRowKeysInFilter((prev) => updater(prev));
              } else {
                setManualExpandedRowKeys((prev) => updater(prev));
              }
            };
            if (expanded) {
              updateExpandedKeys((prev) => Array.from(new Set([...prev, recordId])));
              return;
            }

            const toRemove = new Set<string>([recordId]);
            const stack = [...(childrenById.get(recordId) ?? [])];
            while (stack.length > 0) {
              const childId = stack.pop()!;
              if (toRemove.has(childId)) continue;
              toRemove.add(childId);
              (childrenById.get(childId) ?? []).forEach((nextId) => stack.push(nextId));
            }
            updateExpandedKeys((prev) => prev.filter((key) => !toRemove.has(String(key))));
          },
        }}
        size="middle"
        tableLayout="fixed"
        className="mb-4"
      />

      <ResourceDrawer
        visible={drawerVisible}
        mode={drawerMode}
        onClose={() => setDrawerVisible(false)}
        onSuccess={handleRefresh}
        defaultParentId={drawerParentId}
        parentDisplayName={drawerParentDisplayName}
        editRecord={drawerMode === 'edit' ? editRecord : null}
      />

      <ResourceAccountsDrawer
        visible={accountsDrawerVisible}
        resourceCode={accountsDrawerResourceCode}
        resourceName={accountsDrawerResourceName}
        onClose={() => setAccountsDrawerVisible(false)}
      />
    </div>
  );
};

export default ResourcePage;

